import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { useLoadingIndicator } from '../../../hooks/useLoadingIndicator';
import { useToastMessage } from '../../../hooks/useToastMessage';
import {
	GetBatchesResponse,
	PagingConfig,
	BatchFilter,
} from '../../../shared/PreWorkServiceTypes';
import { getBatches } from '../../../service/PreWorkService';
import { DEFAULT_PAGE_SIZE } from '../../../shared/Constants';

export const useBatchViewPrework = () => {
	const { srcmId } = useAuth();
	const { setLoading } = useLoadingIndicator();
	const { showToastMessage } = useToastMessage();
	const [candidateBatchData, setCandidateBatchData] =
		useState<GetBatchesResponse>({
			batches: [],
			pagination: { totalNoOfRecords: 0 },
		});
	const [candidateBatchFilter, setCandidateBatchFilter] = useState<BatchFilter>(
		{ isPreworkEnabled: true }
	);
	const [batchPaging, setBatchPaging] = useState<PagingConfig>({});

	const onBatchViewFilterChange = useCallback((filterParam: BatchFilter) => {
		setCandidateBatchFilter(filterParam);
	}, []);

	const onBatchPageChange = useCallback(
		(pageNumber: number, pageSize: number) => {
			setBatchPaging({ pageNo: pageNumber, pageSize });
		},
		[]
	);

	const fetchBatchViewData = useCallback(
		async (pageNumber: number, pageSize: number, filter: BatchFilter) => {
			if (srcmId) {
				setLoading(true);
				const pageIndex = pageNumber < 1 ? 0 : pageNumber - 1;
				try {
					const batchDataWithoutKey = await getBatches({
						functionarySrcmId: srcmId,
						pageIndex,
						pageSize,
						batchFilter: filter,
					});
					const batchData = {
						...batchDataWithoutKey,
						...(batchDataWithoutKey.batches && {
							candidateBatches: batchDataWithoutKey.batches.map((batch) => ({
								...batch,
								key: batch.id,
							})),
						}),
					};

					if (
						batchData.pagination.totalNoOfPages &&
						batchData.pagination.totalNoOfPages > 0 &&
						pageNumber > batchData.pagination.totalNoOfPages
					) {
						onBatchPageChange(
							batchData.pagination.totalNoOfPages ?? 1,
							pageSize
						);
					} else {
						setCandidateBatchData(batchData);
					}
				} catch (e) {
					if (e instanceof Error) {
						showToastMessage('error', e.message);
					} else {
						showToastMessage('error', 'Unknown error occurred');
					}
				} finally {
					setLoading(false);
				}
			}
		},
		[srcmId, setLoading, showToastMessage, onBatchPageChange]
	);

	useEffect(() => {
		void fetchBatchViewData(
			batchPaging.pageNo ?? 1,
			batchPaging.pageSize ?? DEFAULT_PAGE_SIZE,
			candidateBatchFilter
		);
	}, [candidateBatchFilter, fetchBatchViewData, batchPaging]);

	return {
		batchPaging,
		candidateBatchFilter,
		candidateBatchData,
		onBatchViewFilterChange,
		onBatchPageChange,
	};
};
